import {Component, forwardRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Observable, of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {AsyncValidator, ValidatorGenerator} from '../../utils';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors
} from '@angular/forms';
import {WidgetValidator} from '@pt/pt-models';
import {CheckEmailService} from '@pt/api-client';

@Component({
  selector: 'pt-input-email',
  templateUrl: './input-email.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputEmailComponent)
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => InputEmailComponent),
      multi: true
    }
  ]
})
export class InputEmailComponent implements OnInit, OnDestroy, OnChanges, ControlValueAccessor, AsyncValidator {

  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() currentUserEmail: string;
  @Input() label: string;
  @Input() placeholder: string;
  @Input() checkEmailService: CheckEmailService;
  @Input() help: string;


  public formGroup: FormGroup;
  isChanged = false;
  isRequired: boolean;
  inputId: string;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.validators) {
      this.setFormGroup(true);
    } else {
      this.setFormGroup(false);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.inputId = 'ptWInputEmail_' + this.controlName;
  }

  writeValue(obj: any): void {
    this.formGroup.controls.value.setValue(obj, {emitEvent: false, onlySelf: true});
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.formGroup.disable({emitEvent: false, onlySelf: true});
    } else {
      this.formGroup.enable({emitEvent: false, onlySelf: true});
    }
  }

  validate(c: AbstractControl): Observable<ValidationErrors | null> {
    // must return result wrapped in Observable
    return of(this.formGroup.invalid ? {invalid: true} : null);
  }

  private setFormGroup(changeValidator?: boolean) {
    this.destroy$.next(true);
    const validator = ValidatorGenerator.generateValidator(this.validators);
    const asyncValidator = this.checkEmailService ? AsyncValidator.checkEmail(this.checkEmailService, this.currentUserEmail) : null;
    this.isRequired = ValidatorGenerator.validatorsContainsRequired(this.validators);
    this.formGroup = new FormGroup({
      value: new FormControl(null, validator, asyncValidator)
    });

    this.formGroup.statusChanges.pipe(
      debounceTime(50),
      distinctUntilChanged()
    ).subscribe(() => {
      this.formGroup.updateValueAndValidity();
    });

    this.formGroup.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntil(this.destroy$),
      debounceTime(250),
    ).subscribe(t => {
      this.isChanged = t?.value !== null;
      if (this.onChanged) {
        this.onChanged(t.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    });

    if (changeValidator) {
      this.isChanged = true;
      if (this.onChanged) {
        this.onChanged(this.formGroup.controls.value);
      }
      if (this.onTouched) {
        this.onTouched();
      }
    }
  }
}
